import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircleIcon from "@mui/icons-material/Circle";
import BoltIcon from "@mui/icons-material/Bolt";
import HomeIcon from "@mui/icons-material/Home";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  formatOrgName,
  isSyntheticEmail,
  RolesRow,
} from "pages/components/SidebarOrgUserExtras";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import toast from "react-hot-toast";
import Logo from "assets/images/logos/flex2logo.png";
import {
  BACKOFFICE_ORG_ID,
  ENERGY_SYSTEM_NAME,
  SYSTEM_DISPLAY_NAMES,
  getAppMode,
  getDefaultRoute,
  selectUserDetails,
  systemNameForAppMode,
} from "config/appMode";
import { setMySystemDefaultRoute } from "api/users.api";
import { getUser, logoutUser } from "redux-store/auth/actions";
import { getEnergyPortalRoutes } from "./EnergyPortalNavigation";

// Orange brand accent for the Energy portal — pairs with the BoltIcon
// used in the DefaultSystemPicker. Two-tone tinted dark background
// distinguishes Energy from the navy LCP sidebar + green Environmental.
const ENERGY_ACCENT = "#ff9100";
const ENERGY_ACCENT_HOVER = "#ffb74d";
const ENERGY_BG_TOP = "#1a1612";
const ENERGY_BG_BOTTOM = "#2b1c0b";
const ENERGY_HOVER_BG = "rgba(255,145,0,0.22)";
const ENERGY_ACTIVE_SHADOW = "0 10px 20px rgba(255,145,0,0.28)";

const itemSx = (active, depth) => ({
  mx: 1.25,
  mb: 0.5,
  pl: 1.4 + depth * 1.8,
  pr: 1.25,
  py: depth === 0 ? 1.05 : 0.85,
  borderRadius: "8px",
  color: active ? "#fff" : "rgba(255,255,255,0.72)",
  bgcolor: active ? ENERGY_ACCENT : "transparent",
  boxShadow: active ? ENERGY_ACTIVE_SHADOW : "none",
  "&:hover": {
    bgcolor: active ? ENERGY_ACCENT : ENERGY_HOVER_BG,
    color: "#fff",
  },
  "& .MuiListItemIcon-root": {
    color: "inherit",
    minWidth: depth === 0 ? 34 : 24,
  },
});

const routePattern = (route) => {
  if (!route) return null;
  const escaped = route.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^${escaped.replace(/:[^/]+/g, "[^/]+")}(?:/)?$`);
};

const routeMatches = (route, pathname) => {
  if (!route) return false;
  if (route.includes(":")) return routePattern(route).test(pathname);
  return pathname === route || pathname.startsWith(`${route}/`);
};

const routeHasActiveChild = (route, pathname) => {
  if (routeMatches(route.route, pathname)) return true;
  return (route.collapse || []).some((child) => routeHasActiveChild(child, pathname));
};

const collectActiveKeys = (routeList, pathname, keys = {}) => {
  routeList.forEach((route) => {
    const routeKey = route.key || route.name;
    if (route.collapse && routeHasActiveChild(route, pathname)) {
      keys[routeKey] = true;
      collectActiveKeys(route.collapse, pathname, keys);
    }
  });
  return keys;
};

// Per-system localStorage key so switching systems doesn't leak open-state.
const OPEN_KEYS_STORAGE = "energyPortalSidebarOpenKeys";

const readStoredOpenKeys = () => {
  try {
    const raw = window.localStorage.getItem(OPEN_KEYS_STORAGE);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const writeStoredOpenKeys = (keys) => {
  try {
    window.localStorage.setItem(OPEN_KEYS_STORAGE, JSON.stringify(keys));
  } catch (e) {
    /* localStorage unavailable */
  }
};

/**
 * Sidebar for the Energy system. Parallel of LcpPortalSidebar +
 * EnvironmentalPortalSidebar — same chrome, same home-pin / logout
 * machinery — with the Energy branding (orange BoltIcon, "Energy"
 * label). Mounted by EnergyPortalShell when App.js's pickSidebarVariant
 * returns SIDEBAR_VARIANTS.ENERGY_PORTAL.
 */
function EnergyPortalSidebar({ onNavigate = () => {}, onSwitchSystem }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const user = useSelector((state) => state.auth.user);
  const userDetails = selectUserDetails(user);

  const hasEnergy = useMemo(() => {
    const systems = userDetails?.organizationSystems;
    return Array.isArray(systems) && systems.includes(ENERGY_SYSTEM_NAME);
  }, [userDetails?.organizationSystems]);

  const homeRoute = useMemo(
    () => getDefaultRoute(userDetails) || "/energy/control-center",
    [userDetails]
  );

  // Org-context for per-item gates (Enterprise menu needs role +
  // isEnterpriseOrganization to decide visibility).
  const orgContext = useMemo(
    () => ({
      isEnterpriseOrganization:
        userDetails?.isEnterpriseOrganization === true,
      role: userDetails?.role || "",
    }),
    [userDetails?.isEnterpriseOrganization, userDetails?.role]
  );

  const visibleRoutes = useMemo(() => getEnergyPortalRoutes(null, orgContext), [orgContext]);

  const [openKeys, setOpenKeys] = useState(readStoredOpenKeys);

  useEffect(() => {
    setOpenKeys((current) => ({ ...current, ...collectActiveKeys(visibleRoutes, pathname) }));
  }, [visibleRoutes, pathname]);

  useEffect(() => {
    writeStoredOpenKeys(openKeys);
  }, [openKeys]);

  const handleRoute = (route) => {
    if (route.href) {
      window.open(route.href, "_blank", "noreferrer");
      return;
    }
    if (route.route) {
      navigate(route.route);
      onNavigate();
    }
  };

  const renderRoute = (route, depth = 0) => {
    if (route.type === "title") {
      return (
        <Typography
          key={route.key}
          variant="caption"
          sx={{
            display: "block",
            px: 2.5,
            pt: 2,
            pb: 0.75,
            color: "rgba(255,255,255,0.42)",
            fontSize: 10,
            fontWeight: 800,
            textTransform: "uppercase",
          }}
        >
          {route.title}
        </Typography>
      );
    }

    if (route.type === "divider") {
      return <Divider key={route.key} sx={{ borderColor: "rgba(255,255,255,0.08)", my: 1 }} />;
    }

    const key = route.key || route.name || route.route;
    const active = routeHasActiveChild(route, pathname);
    const hasChildren = route.collapse?.length > 0;
    const isFlatGroup = hasChildren && route.noCollapse && route.route;
    const open = Boolean(openKeys[key]) || isFlatGroup;

    return (
      <React.Fragment key={key}>
        <ListItemButton
          selected={active}
          sx={itemSx(active, depth)}
          onClick={() => {
            if (isFlatGroup || !hasChildren) {
              handleRoute(route);
            } else {
              setOpenKeys((current) => ({ ...current, [key]: !open }));
            }
          }}
        >
          {route.icon && <ListItemIcon>{route.icon}</ListItemIcon>}
          <ListItemText
            primary={route.name}
            primaryTypographyProps={{
              fontSize: depth === 0 ? 13 : 12,
              fontWeight: active ? 800 : 600,
              noWrap: true,
            }}
          />
          {hasChildren && !isFlatGroup &&
            (open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
        </ListItemButton>
        {hasChildren && isFlatGroup && (
          <List disablePadding>
            {route.collapse.map((child) => renderRoute(child, depth + 1))}
          </List>
        )}
        {hasChildren && !isFlatGroup && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List disablePadding>
              {route.collapse.map((child) => renderRoute(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const orgName =
    userDetails?.organizationName ||
    userDetails?.businessName ||
    "Energy";

  const userDisplayName = (() => {
    // firstName/lastName live on the outer UserProfile (user), not on
    // the inner UserDetailsDto (userDetails). Check both.
    const first = (userDetails?.firstName || user?.firstName)?.trim();
    const last = (userDetails?.lastName || user?.lastName)?.trim();
    const full = [first, last].filter(Boolean).join(" ");
    const realEmail = (e) => (isSyntheticEmail(e) ? null : e);
    return (
      full ||
      user?.displayName ||
      userDetails?.displayName ||
      user?.username ||
      userDetails?.username ||
      realEmail(user?.email) ||
      realEmail(userDetails?.email) ||
      "—"
    );
  })();

  const currentSystemName = userDetails
    ? systemNameForAppMode(getAppMode(userDetails))
    : null;
  const pinnedRouteForSystem = currentSystemName
    ? userDetails?.systemDefaultRoutes?.[currentSystemName] || null
    : null;
  const isHomePinned = pinnedRouteForSystem === pathname;
  const showHomePinButton =
    userDetails != null &&
    userDetails.organizationId !== BACKOFFICE_ORG_ID &&
    currentSystemName != null;
  const [pinningHome, setPinningHome] = useState(false);

  const handleSetHome = async () => {
    if (pinningHome || !currentSystemName) return;
    setPinningHome(true);
    try {
      await setMySystemDefaultRoute(currentSystemName, pathname);
      await dispatch(getUser());
      const label =
        SYSTEM_DISPLAY_NAMES[getAppMode(userDetails)] || currentSystemName;
      toast.success(`Set as your ${label} home`);
    } catch (err) {
      console.error("Failed to set home route:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't save your home page. Try again.";
      toast.error(message);
    } finally {
      setPinningHome(false);
    }
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/login");
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(180deg, ${ENERGY_BG_TOP} 0%, ${ENERGY_BG_BOTTOM} 100%)`,
        color: "#fff",
      }}
    >
      <Box sx={{ px: 2, pt: 2.25, pb: 1.5 }}>
        <Box
          component={NavLink}
          to={homeRoute}
          onClick={onNavigate}
          sx={{
            display: "inline-block",
            textDecoration: "none",
            cursor: "pointer",
            "&:hover img": { opacity: 0.85 },
          }}
          aria-label="Go to home"
        >
          <Box
            component="img"
            src={Logo}
            alt="Flex2"
            sx={{ width: 160, maxWidth: "100%", display: "block" }}
          />
        </Box>
        {hasEnergy && (
          <Box
            component={onSwitchSystem ? "button" : "div"}
            type={onSwitchSystem ? "button" : undefined}
            onClick={onSwitchSystem}
            aria-label={onSwitchSystem ? "Switch to a different system" : undefined}
            sx={{
              // Block-level flex (not inline-flex) so the badge sits on
              // its own row beneath the logo and starts flush at the
              // container's left padding. Inline-flex was inheriting the
              // browser <button> default text-align which pushed the
              // icon+label off-axis from the LCP/Environmental sidebars.
              mt: 0.75,
              display: "flex",
              width: "fit-content",
              alignItems: "center",
              justifyContent: "flex-start",
              textAlign: "left",
              gap: 0.5,
              color: ENERGY_ACCENT,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              background: "none",
              border: "none",
              m: 0,
              p: 0,
              cursor: onSwitchSystem ? "pointer" : "default",
              "&:hover": onSwitchSystem
                ? { color: ENERGY_ACCENT_HOVER, textDecoration: "underline" }
                : undefined,
            }}
          >
            <BoltIcon sx={{ fontSize: 14 }} />
            Energy
          </Box>
        )}
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        <List component="nav" dense disablePadding>
          {visibleRoutes.map((route) => renderRoute(route))}
        </List>
      </Box>
      <Box
        sx={{
          mx: 1.25,
          mb: 2,
          p: 1.25,
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "10px",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.55)",
            fontSize: 9,
            display: "block",
            textTransform: "uppercase",
          }}
        >
          Organization
        </Typography>
        <Typography variant="body2" sx={{ color: "#fff", fontWeight: 800, fontSize: 12 }}>
          {formatOrgName(orgName, userDetails)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            color: "rgba(255,255,255,0.55)",
            fontSize: 9,
            display: "block",
            textTransform: "uppercase",
          }}
        >
          User
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
          <PersonOutlineIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }} />
          <Typography variant="body2" sx={{ color: "#fff", fontWeight: 700, fontSize: 12 }}>
            {userDisplayName}
          </Typography>
        </Box>
        <RolesRow userDetails={userDetails} />
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Chip
            icon={<CircleIcon sx={{ color: "#2ed66f !important" }} />}
            label="Operational"
            size="small"
            sx={{
              height: 22,
              bgcolor: "rgba(46,214,111,0.12)",
              color: "rgba(255,255,255,0.88)",
              fontSize: 10,
              fontWeight: 700,
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            {showHomePinButton ? (
              <Tooltip
                title={
                  isHomePinned
                    ? `This is your ${currentSystemName} home`
                    : `Set this page as my home for ${currentSystemName}`
                }
                placement="top"
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleSetHome}
                    disabled={pinningHome}
                    aria-label="Set as my home"
                    sx={{
                      color: isHomePinned ? "#2ed66f" : "rgba(255,255,255,0.65)",
                      "&:hover": { color: isHomePinned ? "#4be287" : "#fff" },
                    }}
                  >
                    {isHomePinned ? (
                      <HomeIcon fontSize="small" />
                    ) : (
                      <HomeOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <Tooltip title="Sign out" placement="top">
              <IconButton
                size="small"
                onClick={handleLogout}
                aria-label="Logout"
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  "&:hover": { color: ENERGY_ACCENT_HOVER },
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

EnergyPortalSidebar.propTypes = {
  onNavigate: PropTypes.func,
  onSwitchSystem: PropTypes.func,
};

export default EnergyPortalSidebar;
